import React, { useEffect } from "react";
import { useAppState } from "../../Redux/ReduxHooks";
import { OptionalUI } from "../Booking/OptionalParts/OptionalUI";
import { SdkLoadingState } from "./PayPalState";
import { PayPal } from "../PayPal/PayPal";
import { Dispatch } from "../Dispatch";
import braintree from "braintree-web";
import { AddPayPalFunnel } from "./AddPayPalFunnel";
import { GetOrMakeBraintreeClient } from "../Payment/GetOrMakeBraintreeClient";
import { LogEvent } from "../../utils/LogEvent";

/**
 * Manages the deferred loading of the PayPal SDK. 
 * Instead of loading at startup, we wait for a signal from the UI (e.g. pickup address selected).
 * This avoids slowing down the startup experience.
 * This component doesn't render anything; it just receives and sends events.
 */
export const PayPalLoadingWatcher: React.FC = () => {

    const isFeatureEnabled = useAppState(OptionalUI.AddPayPal);
    const loadingState = useAppState(i => i.PayPal.LoadingStatus);
    const hasPickup = useAppState(i => !!i.booking.Locations[0].Address);
    const isAddPaymentDialogOpen = useAppState(i => i.payment.IsAddPaymentDialogOpen);
    const voiceBooking = useAppState(i => i.PhoneToCnp.Booking);

    // pre-existing states that should trigger a load
    useEffect(() => {

        if (hasPickup) {
            Dispatch.PayPal.ReadyToLoad();
        }

        // load the sdk when the user opens Add Payment dialog from the user profile
        if (isAddPaymentDialogOpen) {
            Dispatch.PayPal.ReadyToLoad();
        }

        // No input fields in the voice booking flow except Payment method. Initialise this early so that the user has a better chance of seeing the PayPal option without delay when they click on the payment dropdown.
        if (voiceBooking) {
            Dispatch.PayPal.ReadyToLoad();
        }

    }, [hasPickup, isAddPaymentDialogOpen, voiceBooking]);

    // once only load
    useEffect(() => {

        // PayPal is supported
        if (!isFeatureEnabled) return;

        // UI flow has advanced far enough
        if (loadingState === SdkLoadingState.RequestedNotStarted) {
            StartLoadingPayPalSdk();
        }
    }, [isFeatureEnabled, loadingState]);

    return null;
}

/**
 * Intended to run only once.
 * This method just manages the redux state changes.
 */
async function StartLoadingPayPalSdk() {

    Dispatch.PayPal.LoadStarting();    

    const isSuccess = await TryLoadUpToPayPalSdk();

    if (isSuccess) {
        Dispatch.PayPal.LoadSucceeded();
    }
    else {
        Dispatch.PayPal.LoadFailed();
    }
}

/**
 * Loads the PayPal SDK. There are two parts, each of which could fail:
 * 1) Braintree SDK (including the Client Token from API)
 * 2) PayPal SDK.
 */
async function TryLoadUpToPayPalSdk(): Promise<boolean> {

    LogEvent.PayPalDebug("Starting Braintree/PayPal SDK Load From PayPal");

    // 1) Braintree SDK
    const braintreeClient = await GetOrMakeBraintreeClient();

    if (!braintreeClient) {
        LogEvent.PayPalDebug("Braintree SDK Load Failed");
        return false;
    }

    LogEvent.PayPalDebug("Braintree SDK Load Succeeded");

    LogEvent.PayPalDebug("Starting PayPal SDK Load");

    // 2) PayPal SDK
    let payPalClient: braintree.PayPal;

    try {
        payPalClient = await braintree.paypal.create({
            client: braintreeClient
        });
    }
    catch (error) {
        AddPayPalFunnel.Exception("PayPal Client", error, false);
        LogEvent.PayPalDebug("PayPal SDK Load Failed", { Error: JSON.stringify(error) });
        return false;
    }

    LogEvent.PayPalDebug("PayPal SDK Load Succeeded");

    // OK!
    PayPal.SdkIsReady(braintreeClient, payPalClient);
    return true;
}